import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';

interface IPopoverProps {
  /** Content to show in the popover */
  content: React.ReactNode;
  /** Placement of the popover */
  placement?: 'left' | 'right' | 'top' | 'bottom';
  /** Children element that triggers the popover */
  children: React.ReactNode;
  /** Whether popover is controlled externally */
  controlled?: boolean;
  /** Whether popover is open (for controlled mode) */
  open?: boolean;
  /** Callback when popover state changes */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Custom Popover component
 * Shows content on hover with proper positioning
 */
const CLOSE_DELAY_MS = 200; // Triggerdan popoverga o‘tish uchun vaqt

const Popover: React.FC<IPopoverProps> = ({
  content,
  placement = 'right',
  children,
  controlled = false,
  open: controlledOpen,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOpenState = controlled ? controlledOpen : isOpen;

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  /**
   * Handle mouse enter – popoverni ochish yoki yopilishni bekor qilish
   */
  const handleMouseEnter = () => {
    clearCloseTimeout();
    if (!controlled) {
      setIsOpen(true);
    }
    onOpenChange?.(true);
    updatePosition();
  };

  /**
   * Handle mouse leave – qisqa kechikish bilan yopish (child larga yetib borish mumkin)
   */
  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      if (!controlled) {
        setIsOpen(false);
      }
      onOpenChange?.(false);
      closeTimeoutRef.current = null;
    }, CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return () => clearCloseTimeout();
  }, []);

  /**
   * Update popover position based on trigger element
   */
  const updatePosition = () => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const gap = 8; // Gap between trigger and popover

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'right':
        top = triggerRect.top;
        left = triggerRect.right + gap;
        break;
      case 'left':
        top = triggerRect.top;
        left = triggerRect.left - 175 - gap; // 175 is popover width
        break;
      case 'top':
        top = triggerRect.top - 100 - gap; // 100 is estimated popover height
        left = triggerRect.left;
        break;
      case 'bottom':
        top = triggerRect.bottom + gap;
        left = triggerRect.left;
        break;
      default:
        top = triggerRect.top;
        left = triggerRect.right + gap;
    }

    setPosition({ top, left });
  };

  /**
   * Update position when popover opens
   */
  useEffect(() => {
    if (isOpenState) {
      updatePosition();
    }
  }, [isOpenState, placement]);

  /**
   * Handle scroll and resize events
   */
  useEffect(() => {
    if (!isOpenState) return;

    const handleScroll = () => updatePosition();
    const handleResize = () => updatePosition();

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpenState]);

  return (
    <>
      {/* Trigger element */}
      <Box
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{ display: 'inline-block' }}
      >
        {children}
      </Box>

      {/* Popover content – body ga portal qilib sidebar orqasida qolmasligi uchun */}
      {isOpenState &&
        typeof document !== 'undefined' &&
        createPortal(
          <Box
            ref={popoverRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              zIndex: 1300,
              pointerEvents: 'auto',
            }}
          >
            {content}
          </Box>,
          document.body
        )}
    </>
  );
};

export default Popover;
